"use client"

import { Clock, MapPin, Package, Navigation, Eye } from "lucide-react"

import styles from "../css/driver.module.css"
import StatusPill from "./StatusPill.jsx"

/**
 * One stop in the day's route.
 *
 * `status` is "done" | "current" | "locked" and drives both the visual weight
 * and whether the action button is available — the sequence number stays
 * visible in every state so the driver can see where they are in the route.
 */
export default function StopCard({ stop, status, onOpen }) {
  const items = Array.isArray(stop?.itemsDisplay) ? stop.itemsDisplay : []
  const amount = Number(stop?.amount)
  const showAmount = stop?.paymentType !== "MedicalAid" && Number.isFinite(amount)

  const isDone = status === "done"
  const isCurrent = status === "current"
  const isFailed = status === "failed"

  const cardClass = [
    styles.stopCard,
    isCurrent ? styles.stopCurrent : "",
    isDone ? styles.stopDone : "",
    isFailed ? styles.stopFailed : "",
    status === "locked" ? styles.stopLocked : "",
  ]
    .filter(Boolean)
    .join(" ")

  const ActionIcon = isDone ? Eye : Navigation

  return (
    <li className={cardClass}>
      <div className={styles.stopSequence} aria-hidden="true">
        {stop?.sequence ?? "•"}
      </div>

      <div className={styles.stopHeadline}>
        <div>
          <div className={styles.stopPatient}>{stop?.patient || "Unnamed patient"}</div>
          <div className={styles.stopRef}>
            Stop {stop?.sequence ?? "—"} · {stop?.id || stop?.paymentid}
          </div>
        </div>
        <StatusPill status={status} />
      </div>

      <div className={styles.stopBody}>
        <div className={styles.detailRow}>
          <MapPin size={16} aria-hidden="true" />
          <span>{stop?.address || "No address on file"}</span>
        </div>

        <div className={styles.detailRow}>
          <Clock size={16} aria-hidden="true" />
          <span>
            {stop?.timeWindow || "—"}
            {stop?.etaMin != null && !isDone ? (
              <>
                {" · "}
                <span className={styles.detailStrong}>~{stop.etaMin} min drive</span>
              </>
            ) : null}
          </span>
        </div>

        <div className={styles.detailRow}>
          <Package size={16} aria-hidden="true" />
          <span>{items.length ? items.join(", ") : "No items listed"}</span>
        </div>

        {showAmount && (
          <div className={styles.detailRow}>
            <span className={styles.detailGlyph} aria-hidden="true">
              R
            </span>
            <span className={styles.detailStrong}>
              {amount.toFixed(2)}
              {stop?.paymentType ? ` · ${stop.paymentType}` : ""}
            </span>
          </div>
        )}
      </div>

      {isFailed && stop?.failure && (
        <div className={styles.failureNote}>
          <strong>{stop.failure.reasonLabel || "Not delivered"}</strong>
          {stop.failure.note ? ` — ${stop.failure.note}` : ""}
        </div>
      )}

      {!isFailed && (
        <div className={styles.stopFooter}>
          <button
            type="button"
            className={isCurrent ? styles.primaryBtn : styles.secondaryBtn}
            disabled={status === "locked"}
            onClick={onOpen}
          >
            <ActionIcon size={18} aria-hidden="true" />
            {isDone ? "View delivery" : isCurrent ? "Start delivery" : "Locked"}
          </button>
        </div>
      )}
    </li>
  )
}
